import moment from 'moment';
import React, { createContext, useContext, useEffect, useState } from 'react';
import useRescheduleHistoryActions from '../hooks/APIHooks/appointments/useRescheduleHistoryActions';
import { RescheduleHistory } from '../types/history';
import { User } from '../types/users';
import { useAuthContext } from './AuthProvider';

// datatype of the context declared here
interface IRescheduleHistory {
  isRescheduleHistoryLoading: boolean;
  isRescheduleHistoryFetching: boolean;
  rescheduleHistoryType: string;
  rescheduleHistoryList: Array<RescheduleHistory>;
  rescheduleHistory: RescheduleHistory | null;
  showDetailsModal: boolean;
  showConfirmModal: boolean;
  showRejectModal: boolean;
  setRescheduleHistoryType: React.Dispatch<React.SetStateAction<string>>;
  setRescheduleHistoryList: React.Dispatch<
    React.SetStateAction<Array<RescheduleHistory>>
  >;
  setRescheduleHistory: React.Dispatch<
    React.SetStateAction<RescheduleHistory | null>
  >;
  setShowDetailsModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowConfirmModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowRejectModal: React.Dispatch<React.SetStateAction<boolean>>;
}

// creating the context here
const RescheduleHistoryContext = createContext<IRescheduleHistory | null>(null);

const RescheduleHistoryProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  // integration of react hooks here
  const [rescheduleHistoryType, setRescheduleHistoryType] =
    useState<string>('');
  const [rescheduleHistoryList, setRescheduleHistoryList] = useState<
    Array<RescheduleHistory>
  >([]);
  const [rescheduleHistory, setRescheduleHistory] =
    useState<RescheduleHistory | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showRejectModal, setShowRejectModal] = useState<boolean>(false);

  // integration of context hooks here
  const { currentUser, currentUserType } = useAuthContext();

  // integration of custom API hooks here
  const { getRescheduleHistoryList, getRescheduleHistoryByClientId } =
    useRescheduleHistoryActions(
      currentUserType === 'client'
        ? 'clientRescheduleHistory'
        : 'rescheduleHistoryList',
      rescheduleHistoryType === 'to'
        ? 'assigned_to'
        : rescheduleHistoryType === 'by'
        ? 'assigned_by'
        : 'all',
      currentUserType === 'client' ? currentUser?.id : undefined
    );

  // setting reschedule history type value if user is not admin here
  useEffect(() => {
    if (currentUser && (currentUser as User).role_name !== 'Admin') {
      setRescheduleHistoryType('to');
    }
  }, [currentUser]);

  console.log('TYPE: ', rescheduleHistoryType);

  useEffect(() => {
    if (currentUserType === 'user') {
      if (getRescheduleHistoryList.data?.is_success) {
        setRescheduleHistoryList(
          getRescheduleHistoryList.data.data.map((reschedule) => ({
            ...reschedule,
            previous_date: moment(reschedule.previous_date).format(
              'DD-MM-YYYY'
            ),
            reschedule_date: moment(reschedule.reschedule_date).format(
              'DD-MM-YYYY'
            ),
          }))
        );
      }
    } else {
      if (getRescheduleHistoryByClientId.data?.is_success) {
        setRescheduleHistoryList(
          getRescheduleHistoryByClientId.data.data.map((reschedule) => ({
            ...reschedule,
            previous_date: moment(reschedule.previous_date).format(
              'DD-MM-YYYY'
            ),
            reschedule_date: moment(reschedule.reschedule_date).format(
              'DD-MM-YYYY'
            ),
          }))
        );
      }
    }
  }, [
    currentUserType,
    getRescheduleHistoryList.data,
    getRescheduleHistoryByClientId.data,
  ]);

  useEffect(() => {
    if (rescheduleHistoryType) {
      getRescheduleHistoryList.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescheduleHistoryType]);

  console.log('RESCHEDULE HISTORY LIST: ', rescheduleHistoryList);

  // rendering the context provider here
  return (
    <RescheduleHistoryContext.Provider
      value={{
        isRescheduleHistoryLoading: getRescheduleHistoryList.isLoading,
        isRescheduleHistoryFetching: getRescheduleHistoryList.isFetching,
        rescheduleHistoryType,
        rescheduleHistoryList,
        rescheduleHistory,
        showDetailsModal,
        showConfirmModal,
        showRejectModal,
        setRescheduleHistoryType,
        setRescheduleHistoryList,
        setShowDetailsModal,
        setRescheduleHistory,
        setShowConfirmModal,
        setShowRejectModal,
      }}
    >
      {children}
    </RescheduleHistoryContext.Provider>
  );
};

export default RescheduleHistoryProvider;

// custom hook to access reschedule history context declared here
export const useRescheduleHistoryContext = () => {
  const context = useContext(RescheduleHistoryContext);

  if (!context) {
    throw new Error(
      'useRescheduleHistoryContext must be within RescheduleHistoryProvider!'
    );
  }

  return context;
};
